import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AlertCircle, Archive, FileSpreadsheet, LockKeyhole, RefreshCw, Trash2, Upload } from "lucide-react";
import { CUSTOMER_EVIDENCE_FIELDS, type CustomerEvidenceImport, type EvidenceField, type EvidenceMapping } from "@shared/customer-evidence";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { CustomerEvidenceSummary } from "@/components/customer-evidence-summary";

type Preview = {
  headers: { index: number; label: string }[];
  columnMapping: EvidenceMapping;
  sourcePreset: "zoominfo" | "custom";
  rowCount: number;
  sheetIndex: number;
  mappingSuggested: boolean;
};
type WorksheetChoice = { index: number; label: string };
type PreviewResult = Preview | { worksheets: WorksheetChoice[] };

const FIELD_LABELS: Record<EvidenceField, string> = {
  jobTitle: "Job title", seniority: "Seniority", jobFunction: "Job function",
  department: "Department", industry: "Industry", subIndustry: "Sub-industry",
  employeeRange: "Employee range", revenueRange: "Revenue range", companyName: "Company name",
  companyState: "Company state", companyCountry: "Company country", businessModel: "Business model",
  matchStatus: "Match status", personMoved: "Person moved",
};
async function readResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const body = await response.text().catch(() => "");
    let message = body;
    try {
      const parsed = JSON.parse(body) as { message?: unknown; error?: unknown };
      if (typeof parsed.message === "string") message = parsed.message;
      else if (typeof parsed.error === "string") message = parsed.error;
    } catch {
      // Keep a plain-text API error as-is.
    }
    throw new Error(response.status === 413 ? "File exceeds the 5 MB limit." : message || `Request failed (${response.status})`);
  }
  return response.json() as Promise<T>;
}
function recordFile(form: FormData, file: File) {
  const extension = file.name.toLowerCase().endsWith(".xlsx") ? "xlsx" : "csv";
  form.append("file", file, `evidence.${extension}`);
}
function isWorksheetResult(result: PreviewResult): result is { worksheets: WorksheetChoice[] } {
  return "worksheets" in result;
}
export function CustomerEvidenceTab({ brandId }: { brandId: string }) {
  const { toast } = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [label, setLabel] = useState("");
  const [sourcePreset, setSourcePreset] = useState<"zoominfo" | "custom">("custom");
  const [preview, setPreview] = useState<Preview | null>(null);
  const [mappingOpen, setMappingOpen] = useState(false);
  const [mappingEdited, setMappingEdited] = useState(false);
  const [worksheets, setWorksheets] = useState<WorksheetChoice[]>([]);
  const [sheetIndex, setSheetIndex] = useState<number | null>(null);
  const [confirmImport, setConfirmImport] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<CustomerEvidenceImport | null>(null);
  const previewController = useRef<AbortController | null>(null);
  const previewRevision = useRef(0);
  const base = `/api/brands/${brandId}/customer-evidence`;
  const importsKey = [base];

  const { data: imports = [], isLoading, isError, refetch } = useQuery<CustomerEvidenceImport[]>({
    queryKey: importsKey,
    queryFn: async ({ signal }) => readResponse(await fetch(base, { credentials: "include", signal })),
  });
  const previewMutation = useMutation({
    mutationFn: async ({ selectedFile, selectedSheet, revision }: { selectedFile: File; selectedSheet?: number; revision: number }) => {
      const form = new FormData();
      recordFile(form, selectedFile);
      if (selectedSheet !== undefined) form.append("sheetIndex", String(selectedSheet));
      previewController.current?.abort();
      const controller = new AbortController();
      previewController.current = controller;
      const result = await readResponse<PreviewResult>(await fetch(`${base}/preview`, { method: "POST", body: form, credentials: "include", signal: controller.signal }));
      return { result, revision };
    },
    onSuccess: ({ result, revision }) => {
      if (revision !== previewRevision.current) return;
      if (isWorksheetResult(result)) {
        setWorksheets(result.worksheets);
        setPreview(null);
        setSheetIndex(null);
      } else {
        setPreview(result);
        setMappingEdited(false);
        setMappingOpen(result.sourcePreset !== "zoominfo" ||
          (result.columnMapping.jobTitle === undefined && result.columnMapping.industry === undefined));
        setWorksheets([]);
        setSheetIndex(result.sheetIndex);
        setSourcePreset(result.sourcePreset);
      }
    },
    onError: (error: Error) => {
      if (error.name !== "AbortError") toast({ title: "Could not preview file", description: error.message, variant: "destructive" });
    },
  });
  const importMutation = useMutation({
    mutationFn: async () => {
      if (!file || !preview) throw new Error("Choose and preview a file first.");
      const form = new FormData();
      recordFile(form, file);
      form.append("label", label.trim());
      form.append("sourcePreset", sourcePreset);
      form.append("columnMapping", JSON.stringify(preview.columnMapping));
      form.append("sheetIndex", String(preview.sheetIndex));
      return readResponse<CustomerEvidenceImport>(await fetch(`${base}/import`, { method: "POST", body: form, credentials: "include" }));
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: importsKey });
      setFile(null); setLabel(""); setPreview(null); setWorksheets([]); setSheetIndex(null); setConfirmImport(false);
      toast({ title: "Customer evidence imported" });
    },
    onError: (error: Error) => toast({ title: "Import failed", description: error.message, variant: "destructive" }),
  });
  const statusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: "active" | "archived" }) =>
      readResponse(await fetch(`${base}/${encodeURIComponent(id)}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ status }), credentials: "include" })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: importsKey }),
    onError: (error: Error) => toast({ title: "Could not update import", description: error.message, variant: "destructive" }),
  });
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(`${base}/${encodeURIComponent(id)}`, { method: "DELETE", credentials: "include" });
      if (!response.ok) throw new Error(`Delete failed (${response.status})`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: importsKey });
      setDeleteTarget(null);
      toast({ title: "Import deleted" });
    },
    onError: (error: Error) => toast({ title: "Could not delete import", description: error.message, variant: "destructive" }),
  });

  useEffect(() => {
    previewRevision.current++;
    setFile(null); setPreview(null); setWorksheets([]); setSheetIndex(null); setLabel("");
    return () => { previewRevision.current++; previewController.current?.abort(); };
  }, [brandId]);

  const requiredMapped = preview && (preview.columnMapping.jobTitle !== undefined || preview.columnMapping.industry !== undefined);
  const fields = useMemo(() => CUSTOMER_EVIDENCE_FIELDS, []);
  const setMapping = (field: EvidenceField, value: string) => {
    if (!preview) return;
    const columnMapping = { ...preview.columnMapping };
    if (value === "unmapped") delete columnMapping[field];
    else columnMapping[field] = Number(value);
    setPreview({ ...preview, columnMapping });
    setMappingEdited(true);
  };
  const chooseFile = (next: File | null) => {
    previewRevision.current++;
    previewController.current?.abort();
    setConfirmImport(false);
    setMappingOpen(false);
    setMappingEdited(false);
    setFile(null); setPreview(null); setWorksheets([]); setSheetIndex(null);
    if (!next) return;
    const validExtension = /\.(csv|xlsx)$/i.test(next.name);
    if (!validExtension) {
      toast({ title: "Unsupported file", description: "Export as CSV or Excel", variant: "destructive" });
      return;
    }
    if (next.size > 5 * 1024 * 1024) {
      toast({ title: "File is too large", description: "Choose a CSV or Excel file under 5 MB.", variant: "destructive" });
      return;
    }
    setFile(next);
    setLabel((current) => current.trim() ? current : "Customer evidence import");
    previewMutation.mutate({ selectedFile: next, revision: ++previewRevision.current });
  };
  const runPreview = (selectedSheet?: number) => {
    if (file) previewMutation.mutate({ selectedFile: file, selectedSheet, revision: ++previewRevision.current });
  };
  const activeImports = imports.filter((item) => item.status === "active");
  const archivedImports = imports.filter((item) => item.status === "archived");

  return (
    <div className="space-y-5 animate-in fade-in-0 duration-200" data-testid="customer-evidence-tab">
      <Card className="border-border/50">
        <CardHeader className="pb-3">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-primary/10 text-primary p-2"><FileSpreadsheet className="h-4 w-4" /></div>
            <div>
              <CardTitle className="text-base">Customer evidence</CardTitle>
              <CardDescription className="mt-1">Import aggregate customer profiles to inform this brand&apos;s audience decisions.</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2 rounded-lg border border-emerald-600/20 bg-emerald-600/[0.04] p-3 text-xs text-muted-foreground">
            <LockKeyhole className="h-4 w-4 shrink-0 text-emerald-700 dark:text-emerald-400" />
            <p>Names, phones, emails and profile links are never stored.</p>
          </div>
          <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_220px]">
            <label className="flex min-h-28 cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-border/70 bg-muted/15 p-4 text-center transition-colors hover:bg-muted/35">
              <Upload className="mb-2 h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">{file ? "File selected for secure preview" : "Choose a CSV or Excel file"}</span>
              <span className="mt-1 text-[11px] text-muted-foreground">{file ? `${(file.size / 1024).toFixed(0)} KB` : "Maximum 5 MB. File contents are used only for aggregate evidence."}</span>
              <input type="file" accept=".csv,.xlsx" className="sr-only" onChange={(event) => chooseFile(event.currentTarget.files?.[0] ?? null)} />
            </label>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <label htmlFor="evidence-label" className="text-xs font-medium">Import label</label>
                <Input id="evidence-label" value={label} maxLength={120} onChange={(event) => setLabel(event.target.value.slice(0, 120))} placeholder="For example, Q2 customer export" />
                <div className="text-right text-[10px] text-muted-foreground">{label.length}/120</div>
              </div>
              <Button className="w-full" disabled={!file || previewMutation.isPending || !label.trim()} onClick={() => runPreview()}>
                {previewMutation.isPending ? "Reading and mapping..." : file ? "Re-read file" : "Read and map file"}
              </Button>
            </div>
          </div>
          {worksheets.length > 0 && (
            <div className="flex flex-col gap-2 rounded-lg border bg-muted/15 p-3 sm:flex-row sm:items-center">
              <div className="flex-1"><p className="text-xs font-medium">Choose a worksheet</p><p className="text-[11px] text-muted-foreground">The file must be uploaded again to preview the selected sheet.</p></div>
              <Select value={sheetIndex === null ? "" : String(sheetIndex)} onValueChange={(value) => setSheetIndex(Number(value))}>
                <SelectTrigger className="sm:w-56"><SelectValue placeholder="Select worksheet" /></SelectTrigger>
                <SelectContent>{worksheets.map((sheet) => <SelectItem key={sheet.index} value={String(sheet.index)}>{sheet.label}</SelectItem>)}</SelectContent>
              </Select>
              <Button variant="outline" disabled={sheetIndex === null || previewMutation.isPending || !file} onClick={() => sheetIndex !== null && runPreview(sheetIndex)}>Re-upload &amp; preview</Button>
            </div>
          )}
          {preview && (
            <div className="space-y-3 rounded-lg border border-primary/20 bg-primary/[0.025] p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div><h3 className="text-sm font-semibold">{sourcePreset === "zoominfo" && !mappingEdited ? "Mapped automatically" : "Column mapping"}</h3><p className="text-xs text-muted-foreground mt-1">{preview.rowCount.toLocaleString()} rows detected. No individual records are shown.</p></div>
                <Badge variant="secondary">{sourcePreset === "zoominfo" ? "ZoomInfo format" : "Custom source"}</Badge>
              </div>
              {sourcePreset === "zoominfo" && !mappingEdited && <p className="text-xs text-muted-foreground">ZoomInfo columns are matched for you. No manual mapping is needed.</p>}
              {sourcePreset !== "zoominfo" && preview.mappingSuggested && <p className="text-xs text-muted-foreground">Suggested mappings are editable. Review them before import.</p>}
              <div className="flex flex-wrap gap-1.5" data-testid="evidence-mapped-fields">
                {fields.filter((field) => preview.columnMapping[field] !== undefined).map((field) => (
                  <Badge key={field} variant="outline" className="text-[11px]">{FIELD_LABELS[field]}</Badge>
                ))}
              </div>
              {fields.some((field) => preview.columnMapping[field] === undefined) && (
                <p className="text-xs text-muted-foreground">
                  Not mapped: {fields.filter((field) => preview.columnMapping[field] === undefined).map((field) => FIELD_LABELS[field]).join(", ")}. Optional fields can be left unmapped.
                </p>
              )}
              <p className="text-xs text-muted-foreground">Only persona-relevant audience evidence is imported. Deal amounts, products and close dates are ignored.</p>
              <Button variant="outline" size="sm" aria-expanded={mappingOpen} aria-controls="evidence-mapping-editor" onClick={() => setMappingOpen((open) => !open)}>
                {mappingOpen ? "Hide mappings" : "Adjust mappings"}
              </Button>
              {mappingOpen && <div id="evidence-mapping-editor" className="grid gap-x-4 gap-y-2 sm:grid-cols-2">
                {fields.map((field) => (
                  <div key={field} className="grid grid-cols-[minmax(0,1fr)_minmax(150px,1.1fr)] items-center gap-2">
                    <label htmlFor={`mapping-${field}`} className="truncate text-xs">{FIELD_LABELS[field]}</label>
                    <Select value={preview.columnMapping[field] === undefined ? "unmapped" : String(preview.columnMapping[field])} onValueChange={(value) => setMapping(field, value)}>
                      <SelectTrigger id={`mapping-${field}`} className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unmapped">Unmapped</SelectItem>
                        {preview.headers.map((header) => <SelectItem key={header.index} value={String(header.index)}>{header.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>}
              {!requiredMapped && <p className="flex items-center gap-1.5 text-xs text-destructive"><AlertCircle className="h-3.5 w-3.5" />Map at least Job title or Industry to continue.</p>}
              <div className="flex justify-end gap-2 border-t pt-3">
                <Button variant="ghost" onClick={() => { setPreview(null); setWorksheets([]); }}>Cancel preview</Button>
                <Button disabled={!requiredMapped || !label.trim() || importMutation.isPending} onClick={() => setConfirmImport(true)}>Import evidence</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="space-y-2"><div className="h-16 animate-pulse rounded-lg bg-muted/50" /><div className="h-16 animate-pulse rounded-lg bg-muted/50" /></div>
      ) : isError ? (
        <div className="rounded-lg border border-destructive/20 bg-destructive/[0.03] p-5 text-center">
          <p className="text-sm font-medium">Customer evidence could not be loaded</p>
          <Button variant="outline" size="sm" className="mt-3" onClick={() => refetch()}><RefreshCw className="mr-2 h-3.5 w-3.5" />Retry</Button>
        </div>
      ) : imports.length === 0 ? (
        <div className="rounded-lg border border-dashed p-7 text-center">
          <FileSpreadsheet className="mx-auto h-6 w-6 text-muted-foreground/50" />
          <p className="mt-2 text-sm font-medium">No customer evidence yet</p>
          <p className="mt-1 text-xs text-muted-foreground">Preview a CSV or Excel export above to create an aggregate import.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {[{ title: "Active imports", entries: activeImports }, { title: "Archived imports", entries: archivedImports }].map((group) => (
            <section key={group.title} className="space-y-3">
              <div className="flex items-center gap-2"><h3 className="text-sm font-semibold">{group.title}</h3><Badge variant="secondary" className="text-[10px]">{group.entries.length}</Badge></div>
              {group.entries.length === 0 ? <p className="rounded-lg border border-dashed p-4 text-xs text-muted-foreground">No {group.title.toLowerCase()}.</p> : group.entries.map((item) => (
                <Card key={item.id} className="border-border/50">
                  <CardHeader className="space-y-2 pb-2">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2"><CardTitle className="text-sm">{item.label}</CardTitle><Badge variant={item.status === "active" ? "default" : "secondary"} className="text-[10px]">{item.status}</Badge><Badge variant="outline" className="text-[10px]">Window: {item.aggregate.window}</Badge></div>
                        <CardDescription className="mt-1 text-[11px]">{item.sourcePreset === "zoominfo" ? "ZoomInfo" : "Custom source"} · Imported {new Date(item.importedAt).toLocaleDateString()} · {item.rowCount.toLocaleString()} source rows</CardDescription>
                      </div>
                      <div className="flex shrink-0 gap-1">
                        <Button variant="outline" size="sm" disabled={statusMutation.isPending} onClick={() => statusMutation.mutate({ id: item.id, status: item.status === "active" ? "archived" : "active" })}>
                          <Archive className="mr-1.5 h-3.5 w-3.5" />{item.status === "active" ? "Archive" : "Activate"}
                        </Button>
                        <Button variant="ghost" size="icon" aria-label={`Delete ${item.label}`} disabled={deleteMutation.isPending} onClick={() => setDeleteTarget(item)}><Trash2 className="h-4 w-4 text-muted-foreground" /></Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent><CustomerEvidenceSummary item={item} /></CardContent>
                </Card>
              ))}
            </section>
          ))}
        </div>
      )}
      <AlertDialog open={confirmImport} onOpenChange={setConfirmImport}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Import aggregate customer evidence?</AlertDialogTitle>
            <AlertDialogDescription>
              Import {preview?.rowCount.toLocaleString()} rows using {Object.keys(preview?.columnMapping ?? {}).length} mapped fields. Confirm that this file is authorized for aggregate analysis. Individual customer records will not be displayed or stored.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Go back</AlertDialogCancel><AlertDialogAction disabled={importMutation.isPending} onClick={(event) => { event.preventDefault(); importMutation.mutate(); }}>{importMutation.isPending ? "Importing..." : "Confirm import"}</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete this evidence import?</AlertDialogTitle>
            <AlertDialogDescription>This permanently deletes the aggregate import <span className="font-medium text-foreground">{deleteTarget?.label}</span>. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" disabled={deleteMutation.isPending} onClick={(event) => { event.preventDefault(); if (deleteTarget) deleteMutation.mutate(deleteTarget.id); }}>{deleteMutation.isPending ? "Deleting..." : "Delete import"}</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
